import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import Editor from "../../../../components/editor/Editor";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import TimedMessage from "../../../../components/timed-message/TimedMessage";
import { FeedbackType, GetSettingsDocument, useSaveSettingsMutation } from "../../../../__generated__/api";
import { SettingsProps } from "../base";
import useArticleContext from "../../../../contexts/HelpContext";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";

const FEEDDBACK_TYPES = {
    [FeedbackType.None]: {
        text: "None",
        size: "",
        valueLabels: [],
    },
    [FeedbackType.Ces]: {
        text: "Customer Effort Score (CES)",
        size: "",
        valueLabels: [
            "Strongly disagree",
            "Disagree",
            "Somewhat disagree",
            "Neutral",
            "Somewhat agree",
            "Agree",
            "Strongly agree",
        ],
    },
    [FeedbackType.Nps]: {
        text: "Net Promoter Score (NPS)",
        size: "fs-4",
        valueLabels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    },
    [FeedbackType.Thumbs]: {
        text: "Thumbs Up/Thumbs Down (CSAT)",
        size: "fs-4",
        valueLabels: [
            "👎",
            "👍",
        ],
    },
    /// /:angry::white_frowning_face::neutral_face::slightly_smiling_face::star-struck:

    [FeedbackType.Faces]: {
        text: "Faces (CSAT)",
        size: "fs-4",
        valueLabels: [
            "😠",
            "🙁",
            "😐",
            "🙂",
            "🤩",
        ],
    },
};

export default function Feedback(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    useArticleContext("urn:fq:article:f9c2e9addfc0407aa06fc692e30b1971");

    const [feedback, setFeedbackType] = useState(props.settings?.feedback?.type);
    const [feedbackMessage, setFeedbackMessage] = useState(props.settings?.feedback?.customMessage ?? "");
    const [shouldAllowComments, setShouldAllowComments] = useState(props.settings?.feedback?.shouldAllowComments ?? true);

    const [responses, setResponses] = useState(props.settings?.feedback?.responses?.map(row => ({ ...row })));

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Feedback saved!",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        feedback: {
                            type: feedback,
                            customMessage: feedbackMessage,
                            shouldAllowComments,
                            responses: responses?.map(({ value, message }) => ({ value, message })) ?? [],
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(feedback, props.settings?.feedback?.type);
    dirtyTracker.useWatchProperty(feedbackMessage, props.settings?.feedback?.customMessage ?? "");
    dirtyTracker.useWatchProperty(shouldAllowComments, props.settings?.feedback?.shouldAllowComments ?? true);
    dirtyTracker.useWatchProperty(
        responses?.map(row => row.value + (row.message ?? "")).join(),
        props.settings?.feedback?.responses?.map(row => row.value + (row.message ?? "")).join(),
    );
    dirtyTracker.useLoad(() => {
        setFeedbackType(props.settings?.feedback?.type);
        setFeedbackMessage(props.settings?.feedback?.customMessage ?? "");
        setShouldAllowComments(props.settings?.feedback?.shouldAllowComments ?? true);
        setResponses(props.settings?.feedback?.responses?.map(row => ({ ...row })));
    }, [props.settings?.feedback]);

    const responseMap = responses?.toIdMap("value") ?? Object.create(null);

    const setResponseType = (value: number, message: string) => {
        const current = responseMap[value];
        if (!current) {
            responseMap[value] = Object.create(null);
        }
        responseMap[value].value = value;
        responseMap[value].message = message;
        setResponses(Object.values(responseMap));
    };

    const feedbackOptions = FEEDDBACK_TYPES[feedback ?? FeedbackType.Thumbs];

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-4 bg-white">

                <h5 className="mb-2 text-light">
                    Feedback
                </h5>
                <Form.Text className="mb-3 d-block">
                    After the ticket is completed and the Agent clicks End Chat (the first time), what type of
                    feedback should be displayed?
                </Form.Text>
                <Form.Group className="mb-3">

                    <Form.Label className="fw-bolder">Type</Form.Label>
                    <Form.Select
                        placeholder="CSAT"
                        onChange={event => setFeedbackType((event.target as HTMLSelectElement).value as FeedbackType)}
                    >
                        {Object.entries(FEEDDBACK_TYPES).map(([key, text]) => (
                            <option value={key} selected={feedback === key}>{text.text}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {(feedback && feedback !== FeedbackType.None) && (
                    <div>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">Custom message</Form.Label>
                            <Form.Control
                                type="text"
                                as="textarea"
                                placeholder="On a scale of 1-5, how do you rate..."
                                onChange={evt => setFeedbackMessage(evt.target.value)}
                                value={feedbackMessage as string}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">

                            <Form.Label className="fw-bolder">Allow Comments</Form.Label>
                            <BooleanSelect
                                onChange={value => setShouldAllowComments(value as boolean)}
                                value={shouldAllowComments}
                            />
                            <Form.Text className="mb-3 d-block">
                                Should the user also be allowed to add a custom message?
                            </Form.Text>
                        </Form.Group>
                    </div>
                )}
            </div>

            {(feedback && feedback !== FeedbackType.None) && (
                <div className="border p-3 rounded mb-4 bg-white">

                    <h5 className="mb-2 text-light">
                        Feedback Messages
                    </h5>
                    <Form.Text className="mb-4 d-block">
                        Customize the message that Agent will display after
                        a user rates the feedback with the answer provided.
                    </Form.Text>

                    {feedbackOptions?.valueLabels.map((label, index) => {
                        const row = responseMap[index];
                        return (
                            <Form.Group className="mb-3 row">
                                <Form.Label className={`col-1  text-center ${feedbackOptions.size}`}>
                                    {label}
                                </Form.Label>

                                <Editor
                                    className="col border border-gray rounded"
                                    text={row?.message as string ?? "<p></p>"}
                                    onChange={data => setResponseType(index, data)}
                                    placeholder="Thanks for your feedback."
                                    shouldFocus={false}
                                />

                            </Form.Group>
                        );
                    })}
                </div>
            )}

            {dirtyTracker.saveComponent}

        </form>
    );
}
